import { useCallback, useState } from 'react';
import { createNextState } from '@reduxjs/toolkit';

/**
 * Drop-in replacement for `use-immer`'s useImmer (kept out of package.json —
 * this app doesn't add new dependencies). `@reduxjs/toolkit` already depends
 * on Immer and re-exports its `produce` as `createNextState`, so this gets
 * the same structural-sharing behavior `use-immer` provided — only the paths
 * a recipe actually touches get new references, everything else keeps its
 * old identity. (An earlier version used `structuredClone`, which clones the
 * whole tree on every update; that broke every `useEffect`/`useMemo`
 * elsewhere in the app depending on an untouched nested object, which look
 * "changed" on every unrelated update — the cause of the infinite
 * update-depth loops.)
 */
export function useImmerState<T>(
  initial: T | (() => T)
): [T, (value: T | ((draft: T) => void)) => void] {
  const [state, setState] = useState<T>(initial);

  const update = useCallback((value: T | ((draft: T) => void)) => {
    if (typeof value === 'function') {
      setState((prev) => createNextState(prev, value as (draft: T) => void));
    } else {
      setState(value);
    }
  }, []);

  return [state, update];
}
